/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { useNavigation, type NavigationProp, type ParamListBase } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useSessionStore } from '../../session/store/useSessionStore';
import { ShellContentFrame } from '../../../shared/layout/ShellContentFrame'
import { ShellTwoColumnRow } from '../../../shared/layout/ShellScrollScreen'
import { useTabWorkspaceLayout } from '../../../shared/layout/useContentLayout';
import { spacing } from '../../../shared/theme/spacing';
import { ensureTicketFonts } from '../../../shared/ticket/ensureTicketFonts'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../../shared/ticket/ticketSurfaces.styles'
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import { SoftPressable } from '../../../shared/ui/SoftPressable';
import { navigateInShell } from '../rootNavigation';
import { useNavVisibilityContext } from '../useNavVisibilityContext';
import {
  findManifestItemByRoute,
  getMobileTabManifestItems,
} from '../navigationManifest';
import { navigateToManifestItemFromMainTabs } from '../navigateFromManifest';
import { AiAssistantBanner } from '../../../home/components/AiAssistantBanner';
import { StoreActivationChecklistCard } from '../../../onboarding/components/StoreActivationChecklistCard';
import { WeekOneSetupCard } from '../../../onboarding/components/WeekOneSetupCard';
import { WeeklyReportsCard } from '../../../onboarding/components/WeeklyReportsCard';
import { OnlineOperatingCard } from '../../../onboarding/components/OnlineOperatingCard';
import { HomeBusyBanner } from '../../../onboarding/components/HomeBusyBanner';
import { Day1NextCard } from '../../../onboarding/components/Day1NextCard';
import { EmployeeReadyCard } from '../../../onboarding/components/EmployeeReadyCard';
import { branchMissingMapLocation } from '../../../settings/domain/branchLocationStatus';
import { formatTurnOpenDuration } from '../../../cash/app-api';
import { setPosNavigationIntent } from '../../../pos/app-api';
import { openCatalogNewProduct, openCatalogNewService } from '../../../catalog/app-api';
import { openCustomerNewCustomer } from '../../../customers/public-api';
import { formatLocalTime } from '@shared/vueltoDateTime';
import { homeCashOpenSubtitle, isCashOpenStale } from './homeCashStatus';
import { HomeAnnouncements } from '../../home/HomeAnnouncements';
import { MarketplaceTrustBanner } from '../../banners/components/MarketplaceTrustBanner';
import { HomeOnboardingCardsProvider } from '../../../onboarding/application/HomeOnboardingCardsContext';
import { OwnerPanelHomeCard } from '../../../owner-panel/components/OwnerPanelHomeCard';
import { usePlanEntitlements } from '../../providers/PlanEntitlementsProvider';
import { useOrganizationLogoNudge } from '../../../onboarding/application/useOrganizationLogoNudge';
import { OrganizationIdentityNudgeCard } from '../../../onboarding/components/OrganizationIdentityNudgeCard';
import { navigateToOrganizationIdentity } from '../../../settings/navigation/openOrganizationIdentity';
import { SoftLimitCreceCard } from '../../billing/components/SoftLimitCreceCard';
import { homeDbg, homeDbgRender } from '../../../shared/dev/homeDebug';
import { useHomeScreenLoad } from './useHomeScreenLoad';

import { formatMoney } from '../../../shared/format/public-api';
type Shortcut = {
  id: string;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  onPress: () => void;
};

function homeFocusToken(suffix: string): string {
  return `home-${suffix}-${Date.now()}`;
}

function inventoryUrgencyParams(filter: 'low' | 'expiring' | 'expired') {
  return {
    initialStockFilter: filter,
    focusToken: homeFocusToken(`stock-${filter}`),
  };
}


function formatClock(iso: string): string {
  return formatLocalTime(iso);
}

/** Skeleton placeholder con pulse sutil — evita layout shift durante la carga inicial. */
const SkeletonBlock = React.memo(function SkeletonBlock({
  height,
  width = '100%',
  radius = 6,
  style,
}: {
  height: number | string;
  width?: number | string;
  radius?: number;
  style?: StyleProp<ViewStyle>;
}) {
  const opacity = useRef(new Animated.Value(0.35)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 0.7, duration: 800, useNativeDriver: true }),
        Animated.timing(opacity, { toValue: 0.35, duration: 800, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [opacity]);
  return (
    <Animated.View
      style={[
        skeletonStyles.block,
        { height, width, borderRadius: radius } as ViewStyle,
        style,
        { opacity },
      ]}
    />
  );
});

export function HomeScreen() {
  homeDbgRender('HomeScreen');

  const navigation = useNavigation<NavigationProp<ParamListBase>>();
  const currentBranch = useSessionStore((s) =>
    s.branches.find((b) => b.id === s.currentBranchId)
  );
  const navCtx = useNavVisibilityContext();
  const { entitlements } = usePlanEntitlements();

  const {
    salesTotal,
    salesCount,
    loadingSales,
    lowStockCount,
    comboLowSellableCount,
    expiringStockCount,
    expiredStockCount,
    purchasesToReceiveCount,
    pendingTasksCount,
    activeCash,
    cashLoaded,
    agendaToday,
    announcements,
    initialLoaded,
    refreshing,
    onRefresh,
    setAnnouncements,
    orgTrust,
    supportUnreadCount,
    refreshSupportUnread,
    pendingOrders,
    day1,
    hasInventory,
    hasAppointments,
    cashModuleAllowed,
    currentBranchId,
    bookingOnly,
    userId,
  } = useHomeScreenLoad();

  const logoNudge = useOrganizationLogoNudge(userId, entitlements?.organization_id);

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const goRoute = useCallback(
    (route: string, params?: object) => {
      const item = findManifestItemByRoute(route);
      if (item) {
        navigateToManifestItemFromMainTabs(navigation, item, params);
        return;
      }
      navigateInShell(navigation, route, params);
    },
    [navigation]
  );

  const cashClosed = cashModuleAllowed && cashLoaded && !activeCash;
  const cashStale = Boolean(activeCash?.openedAt && isCashOpenStale(activeCash.openedAt));
  const cashStatusWarn = cashClosed || cashStale;

  const day1Phase = day1.result?.phase ?? null;
  const day1AwaitingCatalog = Boolean(day1.result?.awaitingCatalog);
  const day1FreshGuide =
    day1Phase === 'need_catalog' ||
    day1Phase === 'need_cash' ||
    day1Phase === 'need_first_sale' ||
    day1Phase === 'need_first_appointment';
  const day1ShowCard =
    day1.isOwner &&
    day1.copy != null &&
    day1.result != null &&
    day1.result.phase !== 'done' &&
    !day1AwaitingCatalog;
  // Día 1 listo alcanza para pintar; no esperar initialLoaded (loadHomeData puede
  // ir atrás o cancelarse). Ventas/announcements llegan después sin bloquear la hoja.
  const homeReady = day1.loaded;

  useEffect(() => {
    homeDbg('HomeScreen', 'day1 state', {
      loaded: day1.loaded,
      phase: day1.result?.phase ?? null,
      mode: day1.result?.mode ?? null,
      skipHeavy: day1.skipHomeHeavyRemoteLoad,
      day1ShowCard,
      day1FreshGuide,
      homeReady,
    });
  }, [
    day1.loaded,
    day1.result?.phase,
    day1.result?.mode,
    day1.skipHomeHeavyRemoteLoad,
    day1ShowCard,
    day1FreshGuide,
    homeReady,
  ]);

  /**
   * Fresh (`need_*`): hoja anclada (hero + Siguiente con camino único).
   * - Urgencias stock/compras/mapa: no.
   * - HomeBusy: no.
   * - Checklist activación: visible (sin fantasma).
   */
  const showCashStatusRow =
    homeReady &&
    cashModuleAllowed &&
    cashLoaded &&
    !day1FreshGuide;

  /** Pill compacta dentro de la hoja día 1 (proto densidad). */
  const showDay1CashPill = day1FreshGuide && cashModuleAllowed && cashLoaded;
  /** Ops reales (caja/pedidos/stock…). Soporte va en strip aparte — no abre 2-col en día1. */
  const urgencies = useMemo(() => {
    const cards: Array<{ id: string; title: string; subtitle: string; onPress: () => void }> = [];
    // Caja cerrada vive solo en la fila de status (evitar duplicado).
    // Día 1 fresh: la hoja anclada ya muestra la pill de caja — no duplicar urgencia.
    if (!day1FreshGuide) {
      if (
        navCtx.publicStoreFeaturesAllowed &&
        navCtx.allowedModules.has('Orders') &&
        (pendingOrders ?? 0) > 0
      ) {
        const noun = bookingOnly ? 'reserva' : 'pedido';
        const inbox = bookingOnly ? 'Bandeja' : 'Pedidos';
        cards.push({
          id: 'orders',
          title: `${pendingOrders} ${noun}${pendingOrders === 1 ? '' : 's'} pendiente${pendingOrders === 1 ? '' : 's'}`,
          subtitle: `Aceptá o rechazá desde ${inbox}`,
          onPress: () => goRoute('OrdersInbox'),
        });
      }
      // Vencidos primero por severidad: unidades irrecuperables que hay que dar de baja.
      if (navCtx.allowedModules.has('Inventory') && (expiredStockCount ?? 0) > 0) {
        cards.push({
          id: 'stock-expired',
          title: `${expiredStockCount} producto${expiredStockCount === 1 ? '' : 's'} vencido${expiredStockCount === 1 ? '' : 's'}`,
          subtitle: 'Tienen unidades vencidas — revisá Stock',
          onPress: () => goRoute('Inventory', inventoryUrgencyParams('expired')),
        });
      }
      if (navCtx.allowedModules.has('Inventory') && (lowStockCount ?? 0) > 0) {
        cards.push({
          id: 'stock-low',
          title: `${lowStockCount} producto${lowStockCount === 1 ? '' : 's'} con stock bajo`,
          subtitle: 'Revisá existencias en Stock',
          onPress: () => goRoute('Inventory', inventoryUrgencyParams('low')),
        });
      }
      if (navCtx.allowedModules.has('Inventory') && (comboLowSellableCount ?? 0) > 0) {
        cards.push({
          id: 'combo-low-sellable',
          title: `${comboLowSellableCount} combo${comboLowSellableCount === 1 ? '' : 's'} con poco stock`,
          subtitle: 'Te queda poco para vender — mirá insumos en Catálogo o Stock',
          onPress: () => goRoute('Catalog'),
        });
      }
      if (navCtx.allowedModules.has('Purchases') && (lowStockCount ?? 0) > 0) {
        cards.push({
          id: 'purchases-restock',
          title: 'Reposición en Compras',
          subtitle: 'Armá una OC con tu proveedor',
          onPress: () =>
            goRoute('Purchases', {
              openDirect: true,
              focusToken: homeFocusToken('purchases-restock'),
            }),
        });
      }
      if (navCtx.allowedModules.has('Inventory') && (expiringStockCount ?? 0) > 0) {
        cards.push({
          id: 'stock-expiring',
          title: `${expiringStockCount} producto${expiringStockCount === 1 ? '' : 's'} por vencer`,
          subtitle: 'Próximos 7 días — mirá Stock',
          onPress: () => goRoute('Inventory', inventoryUrgencyParams('expiring')),
        });
      }
      if (navCtx.allowedModules.has('Purchases') && (purchasesToReceiveCount ?? 0) > 0) {
        cards.push({
          id: 'purchases-receive',
          title: `${purchasesToReceiveCount} orden${purchasesToReceiveCount === 1 ? '' : 'es'} por recibir`,
          subtitle: 'Recepcioná mercadería en Compras',
          onPress: () =>
            goRoute('Purchases', {
              initialQueueFilter: 'to_receive',
              focusToken: homeFocusToken('purchases-receive'),
            }),
        });
      }
      if (navCtx.allowedModules.has('Customers') && (pendingTasksCount ?? 0) > 0) {
        cards.push({
          id: 'tasks-pending',
          title: `${pendingTasksCount} tarea${pendingTasksCount === 1 ? '' : 's'} pendiente${pendingTasksCount === 1 ? '' : 's'}`,
          subtitle: 'Revisá el seguimiento de clientes',
          onPress: () => goRoute('FollowUpInbox'),
        });
      }
      if (
        !day1.isOwner &&
        navCtx.publicStoreFeaturesAllowed &&
        branchMissingMapLocation(currentBranch ?? {})
      ) {
        cards.push({
          id: 'location',
          title: 'Falta ubicar el local en el mapa',
          subtitle: 'Buscá la dirección del local y confirmala en el mapa.',
          onPress: () => goRoute('BranchIdentity'),
        });
      }
    }
    return cards;
  }, [
    day1FreshGuide,
    activeCash,
    bookingOnly,
    cashStale,
    currentBranch,
    goRoute,
    lowStockCount,
    comboLowSellableCount,
    expiringStockCount,
    expiredStockCount,
    purchasesToReceiveCount,
    pendingTasksCount,
    navCtx.allowedModules,
    navCtx.publicStoreFeaturesAllowed,
    pendingOrders,
    day1.isOwner,
  ]);

  const supportUnread = supportUnreadCount ?? 0;
  const supportStrip =
    supportUnread > 0 ? (
      <SoftPressable
        style={[ticketSurfaces.paperSheet, styles.supportStrip]}
        onPress={() => goRoute('SupportReports')}
        accessibilityRole="button"
        accessibilityLabel={`${supportUnread} respuesta${supportUnread === 1 ? '' : 's'} en soporte`}
        testID="home.support.strip"
      >
        <View style={styles.supportDot} />
        <View style={styles.supportText}>
          <Text style={styles.supportTitle}>
            {supportUnread} respuesta{supportUnread === 1 ? '' : 's'} en soporte
          </Text>
          <Text style={styles.supportSub}>El equipo te escribió · abrí el hilo</Text>
        </View>
        <Ionicons name="chevron-forward" size={18} color={ticketColors.ink2} />
      </SoftPressable>
    ) : null;

  const shortcuts = useMemo((): Shortcut[] => {
    const tabRoutes = new Set(getMobileTabManifestItems(navCtx).map((i) => i.route));
    const caps = navCtx.effectiveCapabilities;
    const appointments = Boolean(caps?.has('appointments'));
    const inboxLabel =
      appointments && !caps?.has('delivery_fulfillment') ? 'Bandeja' : 'Pedidos';

    type Candidate = {
      route: string;
      label: string;
      icon: keyof typeof Ionicons.glyphMap;
      visible: boolean;
    };

    const pos: Candidate = {
      route: 'POS',
      label: 'POS',
      icon: 'cart-outline',
      visible: navCtx.allowedModules.has('POS'),
    };
    // Agenda = centro del día en salón: atajo siempre (como POS), aunque ya esté en tabs.
    const agenda: Candidate = {
      route: 'Appointments',
      label: 'Agenda',
      icon: 'calendar-outline',
      visible: appointments && navCtx.allowedModules.has('Agenda'),
    };
    const inbox: Candidate = {
      route: 'OrdersInbox',
      label: inboxLabel,
      icon: 'bag-outline',
      visible:
        navCtx.publicStoreFeaturesAllowed &&
        (navCtx.allowedModules.has('Orders') || appointments) &&
        !tabRoutes.has('OrdersInbox'),
    };
    const catalog: Candidate = {
      route: 'Catalog',
      label: 'Catálogo',
      icon: 'pricetags-outline',
      visible: navCtx.allowedModules.has('Catalog') && !tabRoutes.has('Catalog'),
    };
    const clients: Candidate = {
      route: 'Customers',
      label: 'Clientes',
      icon: 'people-outline',
      visible: navCtx.customerTabAllowed && navCtx.allowedModules.has('Customers') && !tabRoutes.has('Customers'),
    };
    const cash: Candidate = {
      route: 'CashManagement',
      label: 'Caja',
      icon: 'cash-outline',
      visible: navCtx.allowedModules.has('Cash'),
    };
    const store: Candidate = {
      route: 'StorefrontHub',
      label: 'Tienda',
      icon: 'storefront-outline',
      visible: navCtx.publicStoreFeaturesAllowed && navCtx.allowedModules.has('BranchSettings'),
    };

    // appointments: Agenda → Clientes → Caja → POS (walk-in/cobro). Retail: POS primero.
    const candidates: Candidate[] = appointments
      ? [agenda, clients, cash, pos, inbox, store, catalog]
      : [pos, inbox, catalog, clients, cash, store];

    return candidates
      .filter((c) => c.visible)
      .slice(0, 6)
      .map((c) => ({
        id: c.route,
        label: c.label,
        icon: c.icon,
        onPress: () => goRoute(c.route),
      }));
  }, [goRoute, navCtx]);

  /**
   * Acciones operando — verbos de creación, pool único gateado por capability
   * (sin forks por rubro). Primaria = flujo dominante: agenda → OT → mostrador.
   * «Reponer» se resuelve por capability: compra formal o ingreso directo.
   */
  const homeActions = useMemo((): Array<{ id: string; label: string; primary?: boolean; onPress: () => void }> => {
    const caps = navCtx.effectiveCapabilities;
    const mods = navCtx.allowedModules;
    const actions: Array<{ id: string; label: string; primary?: boolean; onPress: () => void }> = [];

    const sale = { id: 'sale', label: 'Nueva venta', onPress: () => goRoute('POS') };
    const appointment = {
      id: 'appointment',
      label: 'Nuevo turno',
      onPress: () => goRoute('Appointments', { openCreate: true }),
    };
    const workOrder = {
      id: 'workOrder',
      label: 'Nueva orden',
      onPress: () => goRoute('WorkOrderForm'),
    };

    const canSell = mods.has('POS');
    const canAgenda = Boolean(caps?.has('appointments')) && mods.has('Agenda');
    const canWorkOrders = Boolean(caps?.has('work_orders')) && mods.has('WorkOrders');

    const primary = canAgenda ? appointment : canWorkOrders ? workOrder : canSell ? sale : null;
    if (primary) actions.push({ ...primary, primary: true });
    if (canSell && primary?.id !== sale.id) actions.push(sale);

    if (caps?.has('external_services') && canSell) {
      actions.push({
        id: 'recarga',
        label: 'Recarga',
        onPress: () => {
          setPosNavigationIntent({ openRecarga: true });
          goRoute('POS');
        },
      });
    }
    if (caps?.has('tables') && mods.has('Tables')) {
      actions.push({
        id: 'reservation',
        label: 'Nueva reserva',
        onPress: () => goRoute('Reservations', { openNew: true }),
      });
    }
    if (mods.has('Expenses')) {
      actions.push({
        id: 'expense',
        label: 'Registrar gasto',
        onPress: () =>
          goRoute('Expenses', { openRegister: true, focusToken: homeFocusToken('expense') }),
      });
    }
    if (caps?.has('customer_credit_fiado') && navCtx.customerTabAllowed && mods.has('Customers')) {
      actions.push({ id: 'fiado', label: 'Anotar fiado', onPress: () => goRoute('Customers') });
    }
    if (mods.has('Purchases')) {
      actions.push({
        id: 'purchase',
        label: 'Nueva compra',
        onPress: () =>
          goRoute('Purchases', { openDirect: true, focusToken: homeFocusToken('purchase') }),
      });
    } else if (caps?.has('inventory') && mods.has('Inventory')) {
      actions.push({
        id: 'stock',
        label: 'Ingresar stock',
        onPress: () =>
          goRoute('Inventory', { openAddStock: true, focusToken: homeFocusToken('stock-add') }),
      });
    }
    if (navCtx.customerTabAllowed && mods.has('Customers')) {
      actions.push({
        id: 'customer',
        label: 'Nuevo cliente',
        onPress: () => openCustomerNewCustomer(navigation),
      });
    }
    if (mods.has('Catalog')) {
      const serviceCatalog = Boolean(caps?.has('service_catalog'));
      actions.push({
        id: 'catalogItem',
        label: serviceCatalog ? 'Nuevo servicio' : 'Cargar producto',
        onPress: () =>
          serviceCatalog
            ? openCatalogNewService(navigation)
            : openCatalogNewProduct(navigation),
      });
    }
    return actions.slice(0, 6);
  }, [goRoute, navigation, navCtx]);

  const { layoutWide } = useTabWorkspaceLayout();
  // Cero-ruido: sin ventas hoy (y sin filtro que las contextúe) la fila se oculta —
  // mismo criterio que la tira de KPIs de Ventas.
  const hasTodaySales = (salesCount ?? 0) > 0 || (salesTotal ?? 0) > 0;
  const agendaHasTurns = Boolean(agendaToday && agendaToday.activeCount > 0);
  const showAgendaBlock =
    hasAppointments && agendaToday != null && (!day1FreshGuide || agendaHasTurns);
  /** 2-col solo con ops reales / agenda / skeleton — no por soporte solo (evita hueco L en día1). */
  const hasSideOps = !homeReady || urgencies.length > 0 || showAgendaBlock;

  // Día 1 fresh: sin datos que consultar — el banner promete vacío; aparece
  // cuando la guía termina (celebrate) o en operando.
  const aiBanner = navCtx.aiNavAllowed && !day1FreshGuide ? (
    <AiAssistantBanner onPress={() => goRoute('AiChat')} />
  ) : null;

  const heroBlock = !homeReady ? (
    <View style={styles.hero}>
      <SkeletonBlock height={12} width={36} />
      <View style={{ height: 6 }} />
      <SkeletonBlock height={24} width={180} />
      <View style={{ height: 8 }} />
      <SkeletonBlock height={30} width={130} />
      <SkeletonBlock height={13} width={70} />
    </View>
  ) : (
    <View style={styles.hero}>
      <Text style={styles.kicker}>Hoy</Text>
      <Text style={styles.branchName} numberOfLines={1}>
        {currentBranch?.name ?? 'Sin sucursal'}
      </Text>
      {loadingSales && salesTotal == null ? (
        <ActivityIndicator color={ticketColors.gold} style={styles.salesSpinner} />
      ) : hasTodaySales ? (
        <View style={styles.salesRow}>
          <Text style={styles.salesValue}>{formatMoney(salesTotal ?? 0)}</Text>
              <Text style={styles.salesMeta}>
                {salesCount ?? 0} venta{(salesCount ?? 0) === 1 ? '' : 's'}
              </Text>
        </View>
      ) : null}
    </View>
  );

  const cashBlock = !homeReady ? (
    cashModuleAllowed ? (
      <View style={styles.statusRow}>
        <SkeletonBlock height={18} width={18} radius={9} />
        <View style={styles.statusText}>
          <SkeletonBlock height={14} width={110} />
          <SkeletonBlock height={12} width={170} />
        </View>
      </View>
    ) : null
  ) :
    showCashStatusRow ? (
      <SoftPressable
        style={[styles.statusRow, cashStatusWarn && styles.statusRowWarn]}
        onPress={() => goRoute('CashManagement')}
        accessibilityRole="button"
        accessibilityLabel={
          activeCash
            ? cashStale
              ? formatTurnOpenDuration(activeCash.openedAt)
              : 'Caja abierta'
            : 'Abrir caja'
        }
      >
        <Ionicons
          name={cashStatusWarn ? 'alert-circle-outline' : 'cash-outline'}
          size={18}
          color={cashStatusWarn ? ticketColors.gold : ticketColors.ink2}
        />
        <View style={styles.statusText}>
          <Text style={styles.statusTitle}>
            {activeCash ? 'Caja abierta' : 'Caja cerrada'}
          </Text>
          <Text style={styles.statusSub}>
            {activeCash
              ? cashStale
                ? `${homeCashOpenSubtitle(activeCash.openedAt)} · cerrá el turno para controlar el arqueo`
                : homeCashOpenSubtitle(activeCash.openedAt)
              : 'Abrí el turno para vender con control'}
          </Text>
        </View>
        <Ionicons name="chevron-forward" size={18} color={ticketColors.ink2} />
      </SoftPressable>
    ) : null;

  const urgenciesBlock = !homeReady ? (
    <View style={styles.section}>
      <SkeletonBlock height={12} width={80} />
      <View style={{ height: 8 }} />
      <View style={[ticketSurfaces.paperSheet, styles.urgencyCard]}>
        <View style={styles.urgencyText}>
          <SkeletonBlock height={15} width={200} />
          <SkeletonBlock height={13} width={140} />
        </View>
      </View>
      <View style={[ticketSurfaces.paperSheet, styles.urgencyCard]}>
        <View style={styles.urgencyText}>
          <SkeletonBlock height={15} width={170} />
          <SkeletonBlock height={13} width={110} />
        </View>
      </View>
    </View>
  ) : (
    urgencies.length > 0 ? (
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>Urgencias</Text>
        {urgencies.map((card) => (
          <SoftPressable
            key={card.id}
            style={[ticketSurfaces.paperSheet, styles.urgencyCard]}
            onPress={card.onPress}
            accessibilityRole="button"
            accessibilityLabel={card.title}
            testID={`home.urgency.${card.id}`}
          >
            <View style={styles.urgencyText}>
              <Text style={styles.urgencyTitle}>{card.title}</Text>
              <Text style={styles.urgencySub}>{card.subtitle}</Text>
            </View>
            <Ionicons name="chevron-forward" size={18} color={ticketColors.ink2} />
          </SoftPressable>
        ))}
      </View>
    ) : null
  );

  const agendaBlock = !homeReady ? null : (
    showAgendaBlock && agendaToday ? (
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>Hoy en agenda</Text>
        <SoftPressable
          style={[ticketSurfaces.paperSheet, styles.urgencyCard]}
          onPress={() => goRoute('Appointments', { focusToday: true })}
          accessibilityRole="button"
          accessibilityLabel="Hoy en agenda"
        >
          <View style={styles.urgencyText}>
            <Text style={styles.urgencyTitle}>
              {agendaToday.activeCount === 0
                ? 'Sin turnos hoy'
                : `${agendaToday.activeCount} turno${agendaToday.activeCount === 1 ? '' : 's'}${
                    agendaToday.pendingCount > 0
                      ? ` · ${agendaToday.pendingCount} pendiente${agendaToday.pendingCount === 1 ? '' : 's'}`
                      : ''
                  }`}
            </Text>
            <Text style={styles.urgencySub} numberOfLines={2}>
              {agendaToday.next
                ? `Próximo ${formatClock(agendaToday.next.startsAt)} · ${agendaToday.next.customerName}`
                : agendaToday.activeCount === 0
                  ? 'Abrí la agenda para cargar turnos'
                  : 'No quedan turnos por venir'}
            </Text>
          </View>
          <Ionicons name="chevron-forward" size={18} color={ticketColors.ink2} />
        </SoftPressable>
      </View>
    ) : null
  );

  const trustBlock = !homeReady ? null : (
    <MarketplaceTrustBanner
      trust={orgTrust.status}
      onPressDetail={() => goRoute('MarketplaceTrust')}
    />
  );

  const day1Block =
    day1ShowCard && day1.copy && day1.result ? (
      <Day1NextCard
        phase={day1.result.phase}
        stepIndex={day1.result.stepIndex}
        stepTotal={day1.result.stepTotal}
        copy={day1.copy}
        steps={day1.result.steps}
        stepLabels={day1.stepLabels}
        showPathList={day1FreshGuide}
        embedded={day1FreshGuide}
        onPrimary={() => {
          const phase = day1.result?.phase;
          if (phase === 'need_catalog') goRoute('Catalog');
          else if (phase === 'need_cash') goRoute('CashManagement');
          else if (phase === 'need_first_appointment')
            goRoute('Appointments', { openCreate: true });
          else if (phase === 'celebrate_first_appointment') goRoute('Appointments');
          else goRoute('POS');
        }}
        onDismiss={
          day1.result.phase === 'celebrate_first_sale' ||
          day1.result.phase === 'celebrate_first_appointment'
            ? () => void day1.dismissPath()
            : undefined
        }
      />
    ) : day1AwaitingCatalog && day1.isOwner && homeReady ? (
      <View style={{ gap: 8, paddingVertical: 4 }} testID="home.day1.skeleton">
        <SkeletonBlock height={12} width={90} />
        <SkeletonBlock height={20} width={160} />
        <SkeletonBlock height={14} width={220} />
        <SkeletonBlock height={14} width={180} />
      </View>
    ) : null;

  const employeeBlock = !homeReady ? null : (
    day1.loaded && !day1.isOwner ? <EmployeeReadyCard /> : null
  );

  const onboardingBlock = !homeReady ? null : (
    userId ? (
    <HomeOnboardingCardsProvider
      userId={userId}
      isOwner={day1.isOwner}
      offerAllowed={!day1FreshGuide}
    >
      {!day1FreshGuide ? <HomeBusyBanner /> : null}
      <WeekOneSetupCard
        userId={userId}
        isOwner={day1.isOwner}
        offerAllowed={!day1FreshGuide}
      />
      <StoreActivationChecklistCard
        userId={userId}
        isOwner={day1.isOwner}
        offerAllowed={!day1FreshGuide}
      />
      <OnlineOperatingCard
        userId={userId}
        isOwner={day1.isOwner}
        offerAllowed={!day1FreshGuide}
      />
      <WeeklyReportsCard
        userId={userId}
        isOwner={day1.isOwner}
        offerAllowed={!day1FreshGuide}
      />
    </HomeOnboardingCardsProvider>
  ) : null
  );

  const announcementsBlock = !homeReady ? null : (
    userId ? (
      <HomeAnnouncements
        items={announcements}
        userId={userId}
        onDismissed={(id) => setAnnouncements((prev) => prev.filter((a) => a.id !== id))}
      />
    ) : null
  );

  const shortcutsBlock =
    !homeReady ? null : (
      // Desktop: el sidebar ya cubre esos destinos (redundante). Mobile: sí
      // ahorra un tap — los tabs solo muestran 4-5 y estos van a lo profundo.
      !day1FreshGuide && !layoutWide && shortcuts.length > 0 ? (
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Atajos</Text>
          <View style={[styles.shortcutGrid, layoutWide && styles.shortcutGridWide]}>
            {shortcuts.map((s) => (
              <SoftPressable
                key={s.id}
                style={[styles.shortcut, layoutWide && styles.shortcutWide]}
                onPress={s.onPress}
                accessibilityRole="button"
                accessibilityLabel={s.label}
              >
                <Ionicons name={s.icon} size={20} color={ticketColors.gold} />
                <Text style={styles.shortcutLabel}>{s.label}</Text>
              </SoftPressable>
            ))}
          </View>
        </View>
      ) : null
    );

  const actionsBlock =
    !homeReady ? null : (
      !day1FreshGuide && homeActions.length > 0 ? (
        <View style={styles.section} testID="home.actions">
          <Text style={styles.sectionLabel}>Acciones</Text>
          <View style={[styles.actionsRow, layoutWide && styles.actionsRowWide]}>
            {homeActions.map((a) => (
              <SoftPressable
                key={a.id}
                style={[styles.actionBtn, a.primary && styles.actionBtnPrimary]}
                onPress={a.onPress}
                accessibilityRole="button"
                accessibilityLabel={a.label}
              >
                {a.primary ? <Ionicons name="add" size={15} color="#FFFFFF" /> : null}
                <Text style={[styles.actionBtnLabel, a.primary && styles.actionBtnLabelPrimary]}>
                  {a.label}
                </Text>
              </SoftPressable>
            ))}
          </View>
        </View>
      ) : null
    );

  const day1Sheet =
    day1FreshGuide ? (
      <PaperSheet testID="home.day1.sheet">
        <View style={styles.sheetHero}>
          <Text style={styles.kicker}>Hoy</Text>
          <Text style={styles.branchName} numberOfLines={1}>
            {currentBranch?.name ?? 'Sin sucursal'}
          </Text>
          <View style={styles.sheetHeroRow}>
            <View style={styles.salesRow}>
              {loadingSales && salesTotal == null ? (
                <ActivityIndicator color={ticketColors.gold} style={styles.salesSpinner} />
              ) : hasTodaySales ? (
                <>
                  <Text style={styles.salesValue}>{formatMoney(salesTotal ?? 0)}</Text>
                  <Text style={styles.salesMeta}>
                    {salesCount ?? 0} venta{(salesCount ?? 0) === 1 ? '' : 's'}
                  </Text>
                </>
              ) : null}
            </View>
            {showDay1CashPill ? (
              <SoftPressable
                style={[styles.cashPill, cashClosed && styles.cashPillWarn]}
                onPress={() => goRoute('CashManagement')}
                accessibilityRole="button"
                accessibilityLabel={activeCash ? 'Caja abierta' : 'Caja cerrada'}
              >
                <Text style={[styles.cashPillText, cashClosed && styles.cashPillTextWarn]}>
                  {activeCash ? 'Caja abierta' : 'Caja cerrada'}
                </Text>
              </SoftPressable>
            ) : null}
          </View>
        </View>
        {day1Block}
      </PaperSheet>
    ) : null;

  const leadColumn = !homeReady ? (
    <PaperSheet style={styles.summarySheet}>
      <View style={styles.summaryHero}>{heroBlock}</View>
      {cashBlock ? <View style={styles.summaryCash}>{cashBlock}</View> : null}
    </PaperSheet>
  ) : day1FreshGuide ? (
    <>
      {day1Sheet}
    </>
  ) : (
    <>
      <PaperSheet style={styles.summarySheet}>
        <View style={styles.summaryHero}>{heroBlock}</View>
        {cashBlock ? <View style={styles.summaryCash}>{cashBlock}</View> : null}
      </PaperSheet>
      {day1Block}
    </>
  );

  return (
    <ShellContentFrame>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.root, layoutWide && styles.rootWide]}
        keyboardShouldPersistTaps="handled"
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[ticketColors.gold]}
            tintColor={ticketColors.gold}
          />
        }
      >
        {aiBanner}

        {supportStrip}

        {layoutWide && hasSideOps ? (
          <ShellTwoColumnRow layoutWide>
            <View style={[styles.wideCol, styles.wideColStack]}>
              {leadColumn}
            </View>
            <View style={[styles.wideCol, styles.wideColStack]}>
              {urgenciesBlock}
              {agendaBlock}
            </View>
          </ShellTwoColumnRow>
        ) : (
          leadColumn
        )}

        {actionsBlock}

        {trustBlock}

        {!homeReady || day1FreshGuide ? null : (
          <>
            {logoNudge.showNudgeCard ? (
              <OrganizationIdentityNudgeCard
                onUploadPress={() =>
                  navigateToOrganizationIdentity(navigation, 'shell', { focusSection: 'logo' })
                }
                onDismiss={() => void logoNudge.dismissCard()}
                dismissing={logoNudge.dismissing}
              />
            ) : null}
            <SoftLimitCreceCard />
            <OwnerPanelHomeCard onPress={() => goRoute('OwnerBranchesPanel')} />
          </>
        )}

        {employeeBlock}

        {layoutWide ? (
          <>
            {onboardingBlock}
            {announcementsBlock}
            {shortcutsBlock}
          </>
        ) : (
          <>
            {onboardingBlock}
            {announcementsBlock}
            {urgenciesBlock}
            {agendaBlock}
            {shortcutsBlock}
          </>
        )}
      </ScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  root: {
    flexGrow: 1,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
    gap: 10,
  },
  rootWide: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    maxWidth: 1100,
    width: '100%',
    alignSelf: 'center',
  },
  wideCol: {
    flex: 1,
    minWidth: 280,
    maxWidth: '100%',
  },
  wideColStack: {
    gap: spacing.lg,
  },
  summarySheet: {
    overflow: 'hidden',
  },
  summaryHero: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  summaryCash: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  hero: {
    gap: 6,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  sheetHero: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
    gap: 4,
  },
  sheetHeroRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginTop: 6,
  },
  cashPill: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: ticketColors.brandSoft,
    borderWidth: 1,
    borderColor: ticketColors.gold,
  },
  cashPillWarn: {
    backgroundColor: ticketColors.warnBg,
    borderColor: ticketColors.warnBorder,
  },
  cashPillText: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
  },
  cashPillTextWarn: {
    color: '#B45309',
  },
  kicker: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontFamily: ticketFonts.sans,
  },
  branchName: {
    fontSize: 22,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  salesSpinner: { marginTop: 8, alignSelf: 'flex-start' },
  salesRow: { gap: 2, marginTop: 4 },
  salesValue: {
    fontSize: 28,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  salesMeta: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 12,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  statusRowWarn: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  statusText: { flex: 1, minWidth: 0, gap: 2 },
  statusTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  statusSub: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  section: { gap: 8 },
  sectionLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontFamily: ticketFonts.sans,
  },
  supportStrip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 12,
    paddingHorizontal: spacing.md,
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.paper,
  },
  supportDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ticketColors.gold,
    flexShrink: 0,
  },
  supportText: { flex: 1, minWidth: 0, gap: 2 },
  supportTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  supportSub: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  urgencyCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 14,
    paddingHorizontal: spacing.md,
  },
  urgencyText: { flex: 1, minWidth: 0, gap: 2 },
  urgencyTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  urgencySub: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  shortcutGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  shortcutGridWide: {
    justifyContent: 'flex-start',
  },
  shortcut: {
    width: '31%',
    minWidth: 96,
    flexGrow: 1,
    alignItems: 'center',
    gap: 6,
    paddingVertical: 14,
    paddingHorizontal: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  shortcutWide: {
    width: 112,
    minWidth: 112,
    flexGrow: 0,
  },
  shortcutLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    textAlign: 'center',
  },
  actionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  actionsRowWide: {
    flexWrap: 'nowrap',
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  actionBtnPrimary: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  actionBtnLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  actionBtnLabelPrimary: {
    color: '#FFFFFF',
  },
});

const skeletonStyles = StyleSheet.create({
  block: {
    backgroundColor: ticketColors.paper2,
  },
});
